import {
  ContentWhatsApp,
  Message,
  PartFamily,
  ReminderMessage,
  Translation,
  WhatsAppMessage,
  WhatsAppQuestion,
} from '@/app/module/campaigns/types'
import { BaseWhatsAppContent, shouldDisplayConfirmation, WhatsAppTab } from '@/app/module/common/whatsapp/helpers'

export type WhatsAppMessageType = WhatsAppMessage | WhatsAppQuestion

export const getTranslatedContent = (item: WhatsAppMessageType, activeLanguage: string, defaultLanguage: string) => {
  if (!activeLanguage || activeLanguage === defaultLanguage) {
    return item.message
  }

  const translation = item.message.translations?.[activeLanguage]
  return {
    text: translation?.text || '',
    image: translation?.image || '',
    audio: translation?.audio || '',
    video: translation?.video || '',
    document: translation?.document || '',
    registerTemplate: item.message.registerTemplate,
  }
}

export const hasButtons = (item: WhatsAppMessageType) => {
  if (!item.hasQuestion) {
    return false
  }
  return !!item.onButtons
}

export const hasList = (item: WhatsAppMessageType) => {
  if (!item.hasQuestion) {
    return false
  }
  return !!item.onList
}

export const toTranslation = (content: ContentWhatsApp): Translation => {
  const translation: Translation = {}

  if (content.audio) {
    translation.audio = content.audio
  }
  if (content.document) {
    translation.document = content.document
  }
  if (content.image) {
    translation.image = content.image
  }
  if (content.text) {
    translation.text = content.text
  }
  if (content.video) {
    translation.video = content.video
  }

  return translation
}

/**
 * Check if the confirmation modal should be displayed when switching tabs in the WhatsApp atom component.
 * This function should be called in the wrapper component to have access to all content translations.
 * Why? Because the WhatsApp atom component only has access to the current content, not the translations.
 * @param targetTab The new tab to be switched to
 * @param currentContent The current visible content from the state of the WhatsApp atom component
 * @param stateContent The in-state content of the wrapper component
 * @param hasButtonOrList
 * @param languageConfig
 * @returns
 */
export const shouldDisplayConfirmationMultilingual = (
  targetTab: WhatsAppTab,
  currentContent: BaseWhatsAppContent,
  stateContent: ContentWhatsApp,
  hasButtonOrList: boolean,
  languageConfig: {
    activeLanguage: string
    defaultLanguage: string
  },
): boolean => {
  const { activeLanguage, defaultLanguage } = languageConfig

  // if there's no other language, just check the current content
  if (!activeLanguage) {
    return shouldDisplayConfirmation(targetTab, currentContent, hasButtonOrList)
  }

  const { translations, ...defaultTranslation } = stateContent
  const allTranslations = {
    ...translations,
  }

  // if the activeLanguage is the defaultLanguage, use currentContent as the default translation
  if (activeLanguage === defaultLanguage) {
    allTranslations[defaultLanguage] = toTranslation(currentContent)
  } else {
    // else, use currentContent as the activeLanguage translation
    allTranslations[activeLanguage] = toTranslation(currentContent)
    allTranslations[defaultLanguage] = toTranslation(defaultTranslation)
  }

  return Object.values(allTranslations).some((content) =>
    shouldDisplayConfirmation(targetTab, content, hasButtonOrList),
  )
}

export const isWhatsApp = (item: Message | ReminderMessage): item is WhatsAppMessageType =>
  !!item && typeof item === 'object' && 'message' in item && 'type' in item && item.type === PartFamily.WhatsApp

// Assuming target tab is not Audio
export const resetTranslationsOnTabChange = (translations: Record<string, Translation>) => {
  const newTranslations: Record<string, Translation> = {}
  Object.keys(translations).forEach((lang) => {
    newTranslations[lang] = {
      ...translations[lang], // we keep text content
      audio: '',
      document: '',
      image: '',
      video: '',
    }
  })

  return newTranslations
}

export const resetContentOnTabChange = (targetTab: WhatsAppTab, item: WhatsAppMessageType): WhatsAppMessageType => {
  if (targetTab === WhatsAppTab.Audio) {
    return {
      ...item,
      message: {
        ...item.message,
        audio: '',
        document: '',
        image: '',
        text: '',
        video: '',
        translations: {},
      },
    }
  }

  const { translations } = item.message
  return {
    ...item,
    message: {
      ...item.message,
      audio: '',
      document: '',
      image: '',
      video: '',
      translations: translations ? resetTranslationsOnTabChange(translations) : undefined,
    },
  }
}

export const getInitialWaTab = (content: ContentWhatsApp): WhatsAppTab => {
  const { audio, document, image, text, video, translations } = content

  // check default language
  if (audio) {
    return WhatsAppTab.Audio
  }
  if (document) {
    return WhatsAppTab.Document
  }
  if (image) {
    return WhatsAppTab.Image
  }
  if (video) {
    return WhatsAppTab.Video
  }
  if (text) {
    return WhatsAppTab.Text
  }

  // check translations
  if (translations) {
    const translationsKeys = Object.keys(translations)
    for (let i = 0; i < translationsKeys.length; i += 1) {
      const lang = translationsKeys[i]
      if (translations[lang].audio) {
        return WhatsAppTab.Audio
      }
      if (translations[lang].document) {
        return WhatsAppTab.Document
      }
      if (translations[lang].image) {
        return WhatsAppTab.Image
      }
      if (translations[lang].video) {
        return WhatsAppTab.Video
      }
      if (translations[lang].text) {
        return WhatsAppTab.Text
      }
    }
  }
  return WhatsAppTab.Text
}
